import { useMemo } from "react";
import { Select, type SelectOption } from "ui";
import { useField } from "../hooks/useField";
import { useFormFieldContext } from "./FormField";

interface SelectFormFieldProps<TLabel> {
  options: SelectOption<TLabel>[];
  renderOption?: (props: TLabel, isSelected: boolean) => React.ReactNode;
  displayedValueKey?: Extract<keyof TLabel, string>;
  placeholder?: string;
  className?: string;
  contentClassName?: string;
  inputClassName?: string;
  placement?: React.ComponentProps<typeof Select>["placement"];
  transform?: {
    value: (value: any) => string;
    onChange: (value: string) => any;
  };
  prefixIcon?: React.ComponentProps<typeof Select>["prefixIcon"];
}

export function SelectFormField<TLabel>({
  options,
  renderOption,
  displayedValueKey,
  className,
  contentClassName,
  inputClassName,
  placeholder,
  placement,
  transform,
  prefixIcon,
}: SelectFormFieldProps<TLabel>) {
  const {
    name,
    disabled,
    readOnly,
    required,
    defaultValue = "",
    validation,
    customValidation,
    descriptionId,
    errorMessageId,
    shouldUnregister,
  } = useFormFieldContext();

  const { ref, value, onChange, onBlur, error } = useField({
    name,
    defaultValue,
    validation,
    customValidation,
    shouldUnregister,
  });

  const selectedOptions = useMemo(() => {
    const _value = transform ? transform.value(value) : String(value);
    return (
      options.find((option) => option[0] === _value) ||
      (["", ""] as SelectOption<TLabel>)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  return (
    <Select<any>
      inputRef={ref}
      id={name}
      name={name}
      className={className}
      contentClassName={contentClassName}
      inputClassName={inputClassName}
      placeholder={placeholder}
      placement={placement}
      options={options}
      selected={selectedOptions}
      onSelect={(value: any) =>
        onChange(
          transform ? transform.onChange(value[0] || "") : value[0] || ""
        )
      }
      onBlur={onBlur}
      error={!!error}
      disabled={disabled || readOnly}
      renderOption={renderOption!}
      displayedValueKey={displayedValueKey!}
      aria-required={required}
      aria-describedby={
        !error ? `${descriptionId}` : `${descriptionId} ${errorMessageId}`
      }
      aria-invalid={!!error}
      prefixIcon={prefixIcon}
    />
  );
}
